/**
 * Iter XX.73 — Feature-flag hook. Lazy, cached, single fetch per app
 * load. Reads /api/flags/me (auth-optional). Never blocks the UI —
 * defaults to `false` while loading, updates once the response lands.
 *
 * Usage:
 *   const loopOn = useFlag("new_improvement_loop_enabled");
 *   if (loopOn) return <LandingLoop />;
 *   return <Landing />;
 */
import { useEffect, useState } from "react";
import api from "./api";

// Module-level cache so multiple components share one fetch per load.
let _cache = null;          // resolved flag map: { flag_name: bool }
let _inflight = null;       // Promise while fetching, or null

function _fetchFlags() {
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  _inflight = api.get("/flags/me")
    .then((r) => {
      _cache = r?.data || {};
      return _cache;
    })
    .catch(() => {
      _cache = {};
      return _cache;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

/** Returns the current value of `flagName`, or `false` while loading. */
export function useFlag(flagName) {
  const [value, setValue] = useState(
    _cache ? Boolean(_cache[flagName]) : false,
  );
  useEffect(() => {
    let cancelled = false;
    _fetchFlags().then((flags) => {
      if (!cancelled) setValue(Boolean(flags[flagName]));
    });
    return () => { cancelled = true; };
  }, [flagName]);
  return value;
}

/**
 * Blocking-safe read. Returns the currently-known value (or false)
 * without triggering a re-render. Used inside event handlers where
 * a hook isn't available.
 */
export function readFlagSync(flagName) {
  return _cache ? Boolean(_cache[flagName]) : false;
}

/** Test hook — clears the cache. Not used in production. */
export function _resetFlagsCacheForTests() {
  _cache = null;
  _inflight = null;
}
